/**
 * Central client-side friendly error mapping (v1).
 * Prefer backend userMessage/code when safe; never surface stack/SQL/config internals.
 */

const UNSAFE_PATTERNS = [
  /password\s+not\s+configured/i,
  /stack\s*trace/i,
  /at\s+\S+\s*\(/i,
  /ECONNREFUSED/i,
  /ETIMEOUT/i,
  /ENOTFOUND/i,
  /sql\s*(server|error|exception)/i,
  /mssql/i,
  /DATABASE_URL/i,
  /SMTP_/i,
  /\.env\b/i,
  /JWT_SECRET/i,
  /internal server/i,
  /JSON storage backend/i,
  /Cannot read propert/i,
  /undefined is not/i,
  /TypeError:/i,
  /ReferenceError:/i,
  /AggregateError/i,
  /node_modules/i,
  /[A-Za-z]:\\/,
  /\/var\/www\//i,
  /Bearer\s+/i,
  /token.*expired/i
];

const CODE_COPY = {
  STALE_COUNTING_PERIOD: {
    title: 'New counting period started',
    message:
      'This count belongs to an older counting period and cannot be submitted. Please refresh and complete the current period count.',
    actionLabel: 'Refresh'
  },
  ACTIVE_BATCH_EXISTS: {
    title: 'Count already submitted',
    message:
      'This site already has a count waiting for manager review. Open My Counts to view the current submission.',
    actionLabel: 'Open My Counts'
  },
  DUPLICATE_SUBMISSION: {
    title: 'Already submitted',
    message: 'This count was already received. Open My Counts to view the current submission.',
    actionLabel: 'Open My Counts'
  },
  MANAGER_ACK_REQUIRED: {
    title: 'Acknowledgement needed',
    message: 'Please add a comment for each highlighted item before completing the review.',
    actionLabel: 'Go to first item'
  },
  COUNT_REVIEW_ALREADY_STARTED: {
    title: 'Under manager review',
    message:
      'This count can no longer be recalled because manager review has already started.',
    actionLabel: 'Refresh'
  },
  COUNT_RECALLED: {
    title: 'Count recalled',
    message:
      'This count has been recalled by the site and is no longer available for review.',
    actionLabel: 'Back to dashboard'
  },
  COUNT_NOT_REVIEWABLE: {
    title: 'Count unavailable',
    message:
      'This count is no longer available for review. Refresh the dashboard to see its latest status.',
    actionLabel: 'Back to dashboard'
  },
  INVALID_REVIEW_QUANTITY: {
    title: 'Invalid quantity',
    message: 'One or more edited quantities are not valid numbers. Fix the highlighted lines and try again.',
    actionLabel: 'Review edits'
  },
  SESSION_EXPIRED: {
    title: 'Session expired',
    message: 'Your session has expired. Please log in again.',
    actionLabel: 'Sign in'
  },
  INVALID_LOGIN: {
    title: 'Sign-in failed',
    message: 'The email or password entered is not correct.',
    actionLabel: 'Try again'
  },
  EXPORT_FAILED: {
    title: 'Export failed',
    message:
      'We could not create the export. Please try again. If the issue continues, contact the developer.',
    actionLabel: 'Try again'
  },
  SUBMISSION_PENDING: {
    title: 'Submission pending',
    message:
      'Your count has been saved on this device and will be sent automatically when the connection returns.',
    actionLabel: 'OK'
  },
  SUBMISSION_PENDING_RETRY: {
    title: 'Submission pending',
    message:
      'Your count is saved on this device. We will keep trying to send it when the connection improves.',
    actionLabel: 'Continue'
  },
  SUBMISSION_FAILED: {
    title: 'Submission could not be sent',
    message:
      'Your count could not be sent. Please check the message below, then try again or contact the developer if the issue continues.',
    actionLabel: 'Try again'
  }
};

const STATUS_COPY = {
  400: {
    title: 'Please check your details',
    message: 'Something in this request needs attention. Please check the details and try again.',
    actionLabel: 'Try again'
  },
  401: {
    title: 'Session expired',
    message: 'Your session has expired. Please log in again.',
    actionLabel: 'Sign in'
  },
  403: {
    title: 'Permission needed',
    message: 'You do not have permission to do this.',
    actionLabel: 'OK'
  },
  404: {
    title: 'Not found',
    message: 'This item is no longer available. Please refresh and try again.',
    actionLabel: 'Refresh'
  },
  409: {
    title: 'Conflict',
    message: 'This action conflicts with the current state. Please refresh and try again.',
    actionLabel: 'Refresh'
  },
  413: {
    title: 'File too large',
    message: 'The file is too large to upload. Please choose a smaller file.',
    actionLabel: 'OK'
  },
  429: {
    title: 'Too many attempts',
    message: 'Too many attempts. Please wait a moment and try again.',
    actionLabel: 'OK'
  },
  500: {
    title: 'Something went wrong',
    message:
      'Something went wrong on our side. Please try again. If the issue continues, contact the developer.',
    actionLabel: 'Try again'
  },
  502: {
    title: 'Service unavailable',
    message: 'The service is temporarily unavailable. Please try again when the connection returns.',
    actionLabel: 'Retry'
  },
  503: {
    title: 'Service unavailable',
    message: 'The service is temporarily unavailable. Please try again when the connection returns.',
    actionLabel: 'Retry'
  }
};

const NETWORK_COPY = {
  title: 'Connection unavailable',
  message:
    'We could not reach the server. Check your connection and try again. Counting work may still be saved on this device.',
  actionLabel: 'Retry'
};

function isUnsafeMessage(text) {
  if (!text || typeof text !== 'string') return true;
  const t = text.trim();
  if (!t) return true;
  if (t.length > 400) return true;
  return UNSAFE_PATTERNS.some((re) => re.test(t));
}

function pickSafeUserMessage(data) {
  if (!data || typeof data !== 'object') return null;
  const candidate =
    (typeof data.userMessage === 'string' && data.userMessage) ||
    (typeof data.message === 'string' && data.message && data.code ? data.message : null);
  if (candidate && !isUnsafeMessage(candidate)) return candidate.trim();
  return null;
}

function inferCodeFromPayload(data, status, context) {
  if (data && typeof data.code === 'string' && data.code.trim()) return data.code.trim();
  const err = data && typeof data.error === 'string' ? data.error : '';
  const ctx = (context && context.kind) || '';

  if (/STALE_COUNTING_PERIOD/i.test(err) || /counting period has changed|period mismatch/i.test(err)) {
    return 'STALE_COUNTING_PERIOD';
  }
  if (/active count awaiting manager review/i.test(err)) return 'ACTIVE_BATCH_EXISTS';
  if (/acknowledgement comments are required/i.test(err)) return 'MANAGER_ACK_REQUIRED';
  if (/can no longer be recalled|manager review has already started/i.test(err)) {
    return 'COUNT_REVIEW_ALREADY_STARTED';
  }
  if (/recalled by the site|no longer available for review/i.test(err)) {
    return /recalled by the site/i.test(err) ? 'COUNT_RECALLED' : 'COUNT_NOT_REVIEWABLE';
  }
  if (/Invalid counted quantity/i.test(err)) return 'INVALID_REVIEW_QUANTITY';
  if (status === 401 && (ctx === 'login' || /invalid credentials/i.test(err))) return 'INVALID_LOGIN';
  if (status === 401) return 'SESSION_EXPIRED';
  if (ctx === 'export') return 'EXPORT_FAILED';
  if (ctx === 'final_submit_pending') return 'SUBMISSION_PENDING_RETRY';
  if (ctx === 'final_submit_failed') return 'SUBMISSION_FAILED';
  return null;
}

function getResponseParts(error) {
  if (!error) return { status: null, data: null, network: false };
  if (error.isAxiosError || error.response || error.request) {
    const status = error.response ? error.response.status : null;
    const data = error.response ? error.response.data : null;
    const network = !error.response;
    return { status, data, network };
  }
  if (typeof error === 'object' && (error.status || error.code || error.userMessage || error.error)) {
    return {
      status: error.status || null,
      data: error,
      network: false
    };
  }
  return { status: null, data: { error: String(error.message || error) }, network: false };
}

function resolveCopy(error, context) {
  const { status, data, network } = getResponseParts(error);
  const code = inferCodeFromPayload(data, status, context);

  if (code && CODE_COPY[code]) {
    const base = CODE_COPY[code];
    const safeUser = pickSafeUserMessage(data);
    return {
      code,
      status,
      title: base.title,
      message: safeUser || base.message,
      actionLabel: base.actionLabel
    };
  }

  if (network || status === 502 || status === 503) {
    const safeUser = pickSafeUserMessage(data);
    return {
      code: network ? 'NETWORK' : String(status),
      status,
      title: network ? NETWORK_COPY.title : STATUS_COPY[status].title,
      message: safeUser || (network ? NETWORK_COPY.message : STATUS_COPY[status].message),
      actionLabel: network ? NETWORK_COPY.actionLabel : STATUS_COPY[status].actionLabel
    };
  }

  if (status && STATUS_COPY[status]) {
    const base = STATUS_COPY[status];
    const safeUser = pickSafeUserMessage(data);
    // Prefer known-safe backend error strings for actionable 400s that aren't unsafe
    let message = safeUser || base.message;
    if (!safeUser && data && typeof data.error === 'string' && !isUnsafeMessage(data.error)) {
      // Keep specific business-rule 400 text when already user-friendly
      if (status === 400 || status === 404 || status === 409 || status === 429) {
        message = data.error.trim();
      }
    }
    return {
      code: data && data.code ? data.code : String(status),
      status,
      title: base.title,
      message,
      actionLabel: base.actionLabel
    };
  }

  const fallback =
    (context && context.fallbackMessage) ||
    'Something went wrong. Please try again. If the issue continues, contact the developer.';
  return {
    code: 'UNKNOWN',
    status,
    title: 'Something went wrong',
    message: fallback,
    actionLabel: 'Try again'
  };
}

export function getFriendlyErrorMessage(error, context) {
  return resolveCopy(error, context).message;
}

export function getFriendlyErrorTitle(error, context) {
  return resolveCopy(error, context).title;
}

export function getErrorActionLabel(error, context) {
  return resolveCopy(error, context).actionLabel;
}

export function getFriendlyError(error, context) {
  return resolveCopy(error, context);
}

export function isTechnicalErrorText(text) {
  return isUnsafeMessage(text);
}

export const ERROR_CODES = Object.keys(CODE_COPY);

export const SESSION_EXPIRED_FLASH_KEY = 'si_session_expired_flash';

export function setSessionExpiredFlash() {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLASH_KEY, '1');
  } catch (_) {
    /* ignore */
  }
}

export function consumeSessionExpiredFlash() {
  try {
    const v = sessionStorage.getItem(SESSION_EXPIRED_FLASH_KEY);
    if (v) sessionStorage.removeItem(SESSION_EXPIRED_FLASH_KEY);
    return v === '1';
  } catch (_) {
    return false;
  }
}

export const SESSION_EXPIRED_MESSAGE = CODE_COPY.SESSION_EXPIRED.message;
export const INVALID_LOGIN_MESSAGE = CODE_COPY.INVALID_LOGIN.message;
export const EXPORT_FAILED_MESSAGE = CODE_COPY.EXPORT_FAILED.message;
